import axios from 'axios'
import { uuid, resolvePhoto } from '@/utils/chat'

const MESSAGES = {
  sent: '.ChatMessageSent',
  read: '.ChatMessagesRead',
  delivered: '.ChatMessagesDelivered',
  edited: '.ChatMessageEdited',
  presence: '.ChatPresenceChanged',
}

const RESYNC_DEBOUNCE_MS = 600
const RESYNC_RETRY_MS = [2000, 5000, 15000, 30000]
const SUBSCRIBE_RETRY_BASE_MS = 2000
const SUBSCRIBE_RETRY_MAX_MS = 30000
const SUBSCRIBE_MAX_ATTEMPTS = 6

// Arquivos pendentes de envio e os vínculos do websocket não são estado da tela: ficam fora do Vuex.
const pendingFiles = new Map()
const realtime = {
  userChannel: null, presenceChannel: null, listeners: [], connection: null, stateHandler: null, wasDown: false, timer: null, subs: [], attempts: {}, failed: {}, retryTimers: [], resyncAttempt: 0, onlineHandler: null,
}

function messageKind(file, voice) {
  if (!file) return 'text'
  if (voice) return 'audio'

  return file.type.startsWith('image/') ? 'image' : 'file'
}

const emptyThread = () => ({
  items: [], hasMore: false, loaded: false, loadingOlder: false, myReadId: 0, peerReadId: 0, peerDeliveredId: 0,
})

function sortConversations(list) {
  // Conversa sem mensagem (recém-aberta) fica no topo; o resto pela última mensagem.
  return list.sort((a, b) => {
    const ta = a.last_message ? new Date(a.last_message.created_at).getTime() : Infinity
    const tb = b.last_message ? new Date(b.last_message.created_at).getTime() : Infinity

    return tb - ta
  })
}

export default {
  namespaced: true,

  state: () => ({
    started: false,
    live: false,
    contacts: [],
    contactsLoaded: false,
    conversations: [],
    conversationsLoaded: false,
    threads: {},
    activeId: null,
    incoming: null,
  }),

  getters: {
    unreadTotal: state => state.conversations.reduce((sum, c) => sum + (c.unread || 0), 0),
    activeConversation: state => state.conversations.find(c => c.id === state.activeId) || null,
    activeThread: state => state.threads[state.activeId] || null,
    onlineIds: state => new Set(state.contacts.map(c => c.user_id)),
    // Presença de quem está na conversa: a lista de contatos (sempre fresca) manda; o `online` da conversa
    // só vale até ela carregar, senão ficaria preso no valor de quando a conversa foi baixada.
    presenceOf: (state, getters) => peer => {
      if (!peer) return 'offline'

      return getters.statusOf(peer.user_id) || (!state.contactsLoaded && peer.online ? 'operating' : 'offline')
    },
    statusOf: state => userId => {
      const contact = state.contacts.find(c => c.user_id === userId)

      return contact ? contact.status : null
    },
  },

  mutations: {
    // Aviso de mensagem que chegou sem o usuário estar olhando a conversa (a notificação reage a isso).
    setIncoming(state, incoming) {
      state.incoming = incoming
    },
    setLive(state, live) {
      state.live = live
    },
    setStarted(state, started) {
      state.started = started
    },
    setContacts(state, contacts) {
      state.contacts = contacts
      state.contactsLoaded = true
    },
    setConversations(state, list) {
      state.conversations = sortConversations(list.slice())
      state.conversationsLoaded = true
    },
    upsertConversation(state, conversation) {
      const index = state.conversations.findIndex(c => c.id === conversation.id)
      if (index === -1) state.conversations.push(conversation)
      else state.conversations.splice(index, 1, { ...state.conversations[index], ...conversation })
      sortConversations(state.conversations)
    },
    patchConversation(state, { id, patch }) {
      const conversation = state.conversations.find(c => c.id === id)
      if (conversation) Object.assign(conversation, patch)
      sortConversations(state.conversations)
    },
    setActive(state, id) {
      state.activeId = id
    },
    ensureThread(state, id) {
      if (!state.threads[id]) state.threads = { ...state.threads, [id]: emptyThread() }
    },
    setThread(state, {
      id, items, hasMore, myReadId, peerReadId, peerDeliveredId,
    }) {
      state.threads[id] = {
        ...state.threads[id], items, hasMore, loaded: true, myReadId, peerReadId, peerDeliveredId,
      }
    },
    setLoadingOlder(state, { id, value }) {
      state.threads[id].loadingOlder = value
    },
    // Página mais recente vinda do servidor por cima do que já está na tela: mantém as páginas antigas
    // já carregadas e as mensagens ainda sem id (enviando/falhas), que o servidor não conhece.
    mergeLatest(state, {
      id, items, myReadId, peerReadId, peerDeliveredId,
    }) {
      const thread = state.threads[id]
      const oldestFresh = items.length ? items[0].id : Infinity
      const older = thread.items.filter(m => m.id && m.id < oldestFresh)
      const pending = thread.items.filter(m => !m.id && !items.some(i => i.client_uuid === m.client_uuid))
      thread.items = older.concat(items, pending)
      thread.myReadId = Math.max(thread.myReadId, myReadId)
      thread.peerReadId = Math.max(thread.peerReadId, peerReadId)
      thread.peerDeliveredId = Math.max(thread.peerDeliveredId, peerDeliveredId)
    },
    prependMessages(state, { id, items, hasMore }) {
      const thread = state.threads[id]
      const known = new Set(thread.items.map(m => m.id))
      thread.items = items.filter(m => !known.has(m.id)).concat(thread.items)
      thread.hasMore = hasMore
    },
    // Mensagem nova: troca a otimista (mesmo client_uuid) ou entra no fim; nunca duplica.
    receiveMessage(state, message) {
      const thread = state.threads[message.conversation_id]
      if (!thread) return
      const index = thread.items.findIndex(m => m.client_uuid === message.client_uuid || (m.id && m.id === message.id))
      if (index === -1) {
        thread.items.push(message)
        return
      }
      const replaced = thread.items[index]
      if (replaced.attachment && replaced.attachment.local) URL.revokeObjectURL(replaced.attachment.url)
      thread.items.splice(index, 1, message)
    },
    patchMessage(state, { id, clientUuid, patch }) {
      const message = state.threads[id].items.find(m => m.client_uuid === clientUuid)
      if (message) Object.assign(message, patch)
    },
    removeMessage(state, { id, clientUuid }) {
      const thread = state.threads[id]
      thread.items = thread.items.filter(m => m.client_uuid !== clientUuid)
    },
    setReadMarks(state, {
      id, myReadId, peerReadId, peerDeliveredId,
    }) {
      const thread = state.threads[id]
      if (!thread) return
      if (myReadId !== undefined) thread.myReadId = Math.max(thread.myReadId, myReadId)
      if (peerReadId !== undefined) thread.peerReadId = Math.max(thread.peerReadId, peerReadId)
      if (peerDeliveredId !== undefined) thread.peerDeliveredId = Math.max(thread.peerDeliveredId, peerDeliveredId)
    },
    reset(state) {
      Object.assign(state, {
        started: false, live: false, contacts: [], contactsLoaded: false, conversations: [], conversationsLoaded: false, threads: {}, activeId: null,
      })
    },
  },

  actions: {
    async start({
      state, commit, dispatch, rootState,
    }) {
      if (state.started || !rootState.user.id) return
      commit('setStarted', true)

      await Promise.all([dispatch('loadConversations'), dispatch('loadContacts')])
      dispatch('bindRealtime')
      // Mensagens que chegaram enquanto este aparelho estava sem conexão (ou fechado) são entregues agora.
      dispatch('ackDelivered')
    },

    stop({ commit, dispatch }) {
      dispatch('unbindRealtime')
      commit('reset')
    },

    async loadContacts({ commit, rootState }) {
      const { data } = await axios.get(`${rootState.api}chat/contacts`)
      commit('setContacts', data.data)
    },

    /** Avisa o servidor que este aparelho recebeu as mensagens (✓✓ cinza para quem enviou). Falhar não afeta nada. */
    async ackDelivered({ rootState }, conversationId = null) {
      try {
        await axios.post(`${rootState.api}chat/delivered`, conversationId ? { conversation_id: conversationId } : {})
      } catch (e) {
        // A próxima conexão ou mensagem repete o aviso.
      }
    },

    async loadConversations({ commit, rootState }) {
      const { data } = await axios.get(`${rootState.api}chat/conversations`)
      commit('setConversations', data.data)
    },

    /** Abre a conversa com um contato (cria se for a primeira vez) e a seleciona. */
    async openWithUser({ commit, dispatch, rootState }, userId) {
      const { data } = await axios.post(`${rootState.api}chat/conversations`, { user_id: userId })
      commit('upsertConversation', data.data)
      await dispatch('select', data.data.id)
    },

    /** Garante a conversa com um contato sem selecioná-la (usado ao encaminhar para quem ainda não tem conversa). */
    async ensureConversation({ commit, rootState }, userId) {
      const { data } = await axios.post(`${rootState.api}chat/conversations`, { user_id: userId })
      commit('upsertConversation', data.data)

      return data.data.id
    },

    async select({ state, commit, dispatch }, id) {
      commit('setActive', id)
      commit('ensureThread', id)
      if (!state.threads[id].loaded) await dispatch('loadMessages', id)
      dispatch('markRead', id)
    },

    deselect({ commit }) {
      commit('setActive', null)
    },

    async loadMessages({ commit, rootState }, id) {
      const { data } = await axios.get(`${rootState.api}chat/conversations/${id}/messages`)
      commit('setThread', {
        id, items: data.data, hasMore: data.has_more, myReadId: data.my_last_read_id, peerReadId: data.peer_last_read_id, peerDeliveredId: data.peer_last_delivered_id,
      })
    },

    async refreshMessages({ commit, rootState }, id) {
      const { data } = await axios.get(`${rootState.api}chat/conversations/${id}/messages`)
      commit('mergeLatest', {
        id, items: data.data, myReadId: data.my_last_read_id, peerReadId: data.peer_last_read_id, peerDeliveredId: data.peer_last_delivered_id,
      })
    },

    /** Rolagem para cima: busca a página anterior pelo cursor (id da mensagem mais antiga). */
    async loadOlder({ state, commit, rootState }, id) {
      const thread = state.threads[id]
      const oldest = thread && thread.items.find(m => m.id)
      if (!thread || thread.loadingOlder || !thread.hasMore || !oldest) return false

      commit('setLoadingOlder', { id, value: true })
      try {
        const { data } = await axios.get(`${rootState.api}chat/conversations/${id}/messages`, { params: { before_id: oldest.id } })
        commit('prependMessages', { id, items: data.data, hasMore: data.has_more })

        return true
      } finally {
        commit('setLoadingOlder', { id, value: false })
      }
    },

    sendText({ dispatch }, { conversationId, body }) {
      return dispatch('send', {
        conversationId, body, file: null, voice: false, duration: null, replyTo: null,
      })
    },

    /** Envio otimista: a bolha aparece na hora e vira definitiva quando a API (ou o websocket) responde. */
    async send({ commit, dispatch, rootState }, {
      conversationId, body, file, voice, duration, replyTo = null,
    }) {
      const clientUuid = uuid()
      const kind = messageKind(file, voice)

      commit('ensureThread', conversationId)
      commit('receiveMessage', {
        id: null,
        conversation_id: conversationId,
        sender_id: rootState.user.id,
        kind,
        body: body || null,
        client_uuid: clientUuid,
        created_at: new Date().toISOString(),
        reply_to: replyTo ? {
          id: replyTo.id, sender_id: replyTo.sender_id, kind: replyTo.kind, body: replyTo.body, attachment_name: replyTo.attachment ? replyTo.attachment.name : null,
        } : null,
        forwarded: false,
        edited_at: null,
        attachment: file ? {
          name: file.name, mime: file.type, size: file.size, duration, url: URL.createObjectURL(file), local: true,
        } : null,
        status: 'sending',
        progress: 0,
      })
      if (file) pendingFiles.set(clientUuid, { file, voice, duration })

      await dispatch('deliver', {
        conversationId, clientUuid, body, replyToId: replyTo ? replyTo.id : null,
      })
    },

    async deliver({ commit, rootState }, {
      conversationId, clientUuid, body, replyToId = null,
    }) {
      const form = new FormData()
      form.append('client_uuid', clientUuid)
      if (body) form.append('body', body)
      if (replyToId) form.append('reply_to_id', String(replyToId))
      const pending = pendingFiles.get(clientUuid)
      if (pending) {
        form.append('file', pending.file, pending.file.name)
        if (pending.voice) form.append('voice', '1')
        if (pending.duration) form.append('duration', String(pending.duration))
      }

      commit('patchMessage', { id: conversationId, clientUuid, patch: { status: 'sending', progress: 0, error: null } })

      try {
        const { data } = await axios.post(`${rootState.api}chat/conversations/${conversationId}/messages`, form, {
          onUploadProgress: e => {
            if (pending && e.total) commit('patchMessage', { id: conversationId, clientUuid, patch: { progress: Math.round((e.loaded / e.total) * 100) } })
          },
        })
        commit('receiveMessage', data.data)
        commit('patchConversation', { id: conversationId, patch: { last_message: data.data } })
        if (pending && pending.file) pendingFiles.delete(clientUuid)
        commit('setReadMarks', { id: conversationId, myReadId: data.data.id })
      } catch (e) {
        const validation = e.response && e.response.status === 422 ? Object.values(e.response.data.errors || {})[0] : null
        commit('patchMessage', { id: conversationId, clientUuid, patch: { status: 'failed', error: validation ? validation[0] : null } })
      }
    },

    retry({ state, dispatch }, { conversationId, clientUuid }) {
      const message = state.threads[conversationId].items.find(m => m.client_uuid === clientUuid)

      return dispatch('deliver', {
        conversationId, clientUuid, body: message && message.body, replyToId: message && message.reply_to ? message.reply_to.id : null,
      })
    },

    discardFailed({ commit }, { conversationId, clientUuid }) {
      pendingFiles.delete(clientUuid)
      commit('removeMessage', { id: conversationId, clientUuid })
    },

    /** Troca o texto de uma mensagem própria. Erro (prazo, mensagem sem texto) volta como mensagem para a tela. */
    async edit({ dispatch, rootState }, { messageId, body }) {
      const { data } = await axios.patch(`${rootState.api}chat/messages/${messageId}`, { body })
      dispatch('onMessageEdited', data.data)
    },

    /** Copia a mensagem para outras conversas; as cópias chegam também pelo websocket e não duplicam. */
    async forward({ commit, rootState }, { messageIds, conversationIds }) {
      const { data } = await axios.post(`${rootState.api}chat/forward`, { message_ids: messageIds, conversation_ids: conversationIds })
      data.data.forEach(message => {
        commit('receiveMessage', message)
        commit('patchConversation', { id: message.conversation_id, patch: { last_message: message } })
      })
    },

    async markRead({ state, commit, rootState }, id) {
      const thread = state.threads[id]
      const conversation = state.conversations.find(c => c.id === id)
      const last = thread && thread.items.filter(m => m.id).slice(-1)[0]
      if (!conversation || !last || (conversation.unread === 0 && thread.myReadId >= last.id)) return

      commit('patchConversation', { id, patch: { unread: 0 } })
      commit('setReadMarks', { id, myReadId: last.id })
      try {
        await axios.post(`${rootState.api}chat/conversations/${id}/read`, { message_id: last.id })
      } catch (e) {
        // O próximo evento ou recarregamento corrige o contador.
      }
    },

    // ── tempo real ────────────────────────────────────────────────────────

    async onMessageSent({
      state, commit, dispatch, rootState,
    }, message) {
      const mine = message.sender_id === rootState.user.id
      const known = state.conversations.find(c => c.id === message.conversation_id)

      if (!known) {
        // Conversa nova: a lista do servidor já traz a última mensagem e o contador certo.
        await dispatch('loadConversations')
        if (!mine) dispatch('ackDelivered', message.conversation_id)

        return
      }
      if (!mine) dispatch('ackDelivered', message.conversation_id)

      const open = state.activeId === message.conversation_id
      if (state.threads[message.conversation_id]) commit('receiveMessage', message)
      const current = state.conversations.find(c => c.id === message.conversation_id)
      const visible = open && document.visibilityState === 'visible'
      commit('patchConversation', {
        id: message.conversation_id,
        patch: { last_message: message, unread: mine || visible ? current.unread : (current.unread || 0) + 1 },
      })

      if (!mine && visible) dispatch('markRead', message.conversation_id)
      if (!mine && !visible) {
        commit('setIncoming', {
          seq: Date.now(),
          conversationId: message.conversation_id,
          peerName: current.peer ? current.peer.name : '',
          peerPhoto: current.peer ? resolvePhoto(rootState.url, current.peer.photo_url) : null,
          message,
        })
      }
    },

    onMessageEdited({ state, commit }, message) {
      commit('receiveMessage', message)
      const conversation = state.conversations.find(c => c.id === message.conversation_id)
      if (conversation && conversation.last_message && conversation.last_message.id === message.id) {
        commit('patchConversation', { id: message.conversation_id, patch: { last_message: message } })
      }
    },

    onMessagesRead({ commit, rootState }, payload) {
      if (payload.user_id === rootState.user.id) {
        // Outra aba/aparelho meu leu: zera o contador aqui também.
        commit('patchConversation', { id: payload.conversation_id, patch: { unread: 0 } })
        commit('setReadMarks', { id: payload.conversation_id, myReadId: payload.last_read_message_id })
      } else {
        commit('setReadMarks', { id: payload.conversation_id, peerReadId: payload.last_read_message_id })
      }
    },

    onMessagesDelivered({ commit, rootState }, payload) {
      if (payload.user_id === rootState.user.id) return
      commit('setReadMarks', { id: payload.conversation_id, peerDeliveredId: payload.last_delivered_message_id })
    },

    /**
     * Volta a sincronizar tudo que o websocket pode ter perdido (queda de conexão, aba que dormiu). Logo após a
     * rede voltar as primeiras requisições podem falhar, então tenta de novo com espera crescente em vez de desistir.
     */
    resync({ state, dispatch }) {
      clearTimeout(realtime.timer)
      realtime.timer = setTimeout(async () => {
        try {
          await Promise.all([dispatch('loadContacts'), dispatch('loadConversations')])
          dispatch('ackDelivered')
          if (state.activeId) {
            await dispatch('refreshMessages', state.activeId)
            dispatch('markRead', state.activeId)
          }
          realtime.resyncAttempt = 0
        } catch (e) {
          const wait = RESYNC_RETRY_MS[realtime.resyncAttempt]
          realtime.resyncAttempt += 1
          if (wait !== undefined) realtime.timer = setTimeout(() => dispatch('resync'), wait)
        }
      }, RESYNC_DEBOUNCE_MS)
    },

    /** O canal falhou ao assinar (ex.: autorização recusada logo após voltar a rede): tenta de novo com espera crescente. */
    retrySubscription(context, name) {
      realtime.failed[name] = true
      const attempt = (realtime.attempts[name] || 0) + 1
      realtime.attempts[name] = attempt
      if (attempt > SUBSCRIBE_MAX_ATTEMPTS || typeof window.Echo === 'undefined') return

      const wait = Math.min(SUBSCRIBE_RETRY_BASE_MS * 2 ** (attempt - 1), SUBSCRIBE_RETRY_MAX_MS)
      realtime.retryTimers.push(setTimeout(() => {
        const { subscription } = window.Echo.private(name)
        if (subscription && !subscription.subscribed) subscription.subscribe()
      }, wait))
    },

    refreshPresence({ dispatch }) {
      clearTimeout(realtime.timer)
      realtime.timer = setTimeout(() => Promise.all([dispatch('loadContacts'), dispatch('loadConversations')]), RESYNC_DEBOUNCE_MS)
    },

    bindRealtime({ commit, dispatch, rootState }) {
      if (typeof window.Echo === 'undefined' || realtime.userChannel) return

      realtime.userChannel = `user.${rootState.user.id}`
      const onSent = payload => dispatch('onMessageSent', payload.message)
      const onRead = payload => dispatch('onMessagesRead', payload)
      const onDelivered = payload => dispatch('onMessagesDelivered', payload)
      const onEdited = payload => dispatch('onMessageEdited', payload.message)
      window.Echo.private(realtime.userChannel)
        .listen(MESSAGES.sent, onSent)
        .listen(MESSAGES.edited, onEdited)
        .listen(MESSAGES.read, onRead)
        .listen(MESSAGES.delivered, onDelivered)
      realtime.listeners.push(
        [realtime.userChannel, MESSAGES.sent, onSent],
        [realtime.userChannel, MESSAGES.read, onRead],
        [realtime.userChannel, MESSAGES.delivered, onDelivered],
        [realtime.userChannel, MESSAGES.edited, onEdited],
      )

      const nodeId = rootState.selected_node && rootState.selected_node.id
      if (nodeId) {
        realtime.presenceChannel = `chat-presence.${nodeId}`
        const onPresence = () => dispatch('refreshPresence')
        window.Echo.private(realtime.presenceChannel).listen(MESSAGES.presence, onPresence)
        realtime.listeners.push([realtime.presenceChannel, MESSAGES.presence, onPresence])
      }

      // Assinatura que falhou e depois deu certo: eventos foram perdidos nesse intervalo, então ressincroniza.
      realtime.listeners.map(([channel]) => channel).filter((name, i, all) => all.indexOf(name) === i).forEach(name => {
        const { subscription } = window.Echo.private(name)
        if (!subscription) return
        const onError = () => dispatch('retrySubscription', name)
        const onOk = () => {
          realtime.attempts[name] = 0
          if (realtime.failed[name]) {
            realtime.failed[name] = false
            dispatch('resync')
          }
        }
        subscription.bind('pusher:subscription_error', onError)
        subscription.bind('pusher:subscription_succeeded', onOk)
        realtime.subs.push([subscription, 'pusher:subscription_error', onError], [subscription, 'pusher:subscription_succeeded', onOk])
      })

      const { connector } = window.Echo
      const connection = connector && connector.pusher && connector.pusher.connection
      if (!connection) return
      realtime.connection = connection
      commit('setLive', connection.state === 'connected')
      realtime.stateHandler = state => {
        commit('setLive', state.current === 'connected')
        if (state.current !== 'connected') {
          realtime.wasDown = true
        } else if (realtime.wasDown) {
          // Eventos perdidos durante a queda: recarrega o que a tela mostra.
          realtime.wasDown = false
          dispatch('resync')
        }
      }
      connection.bind('state_change', realtime.stateHandler)

      // Aba que volta a ficar visível (ou o PC que acordou) pode ter perdido eventos sem a conexão cair.
      realtime.visibilityHandler = () => {
        if (document.visibilityState === 'visible') dispatch('resync')
      }
      document.addEventListener('visibilitychange', realtime.visibilityHandler)

      // O navegador avisa quando a internet volta, mesmo que o websocket ainda não tenha percebido a queda.
      realtime.onlineHandler = () => dispatch('resync')
      window.addEventListener('online', realtime.onlineHandler)
    },

    unbindRealtime() {
      if (typeof window.Echo !== 'undefined') {
        // stopListening e não leave(): o canal do usuário também serve a outros avisos.
        realtime.listeners.forEach(([channel, event, fn]) => window.Echo.private(channel).stopListening(event, fn))
        // O canal de presença é de um nó só: ao trocar de nó o antigo é abandonado.
        if (realtime.presenceChannel) window.Echo.leaveChannel(`private-${realtime.presenceChannel}`)
      }
      if (realtime.connection && realtime.stateHandler) realtime.connection.unbind('state_change', realtime.stateHandler)
      if (realtime.visibilityHandler) document.removeEventListener('visibilitychange', realtime.visibilityHandler)
      clearTimeout(realtime.timer)
      realtime.retryTimers.forEach(clearTimeout)
      realtime.subs.forEach(([subscription, event, fn]) => subscription.unbind(event, fn))
      if (realtime.onlineHandler) window.removeEventListener('online', realtime.onlineHandler)
      Object.assign(realtime, {
        userChannel: null, presenceChannel: null, listeners: [], connection: null, stateHandler: null, wasDown: false, timer: null, visibilityHandler: null, subs: [], attempts: {}, failed: {}, retryTimers: [], resyncAttempt: 0, onlineHandler: null,
      })
    },
  },
}
